/**
 * Plan Manager network configuration flow pure logic (Networks list and Config).
 */

// --- from networkListUtils.js ---

/**
 * Publication-status labels shown in the Status column.
 * @readonly
 * @enum {string}
 */
export const NetworkPublicationStatus = Object.freeze({
    NOT_APPLICABLE: "N/A",
    PUBLISHED: "Published",
    UNPUBLISHED: "Unpublished",
});

/**
 * Tone tokens describing how a network publication status should be visually emphasized.
 * Components map these tokens to their own CSS classes; the util stays free of styling.
 * @readonly
 * @enum {string}
 */
export const NetworkStatusTone = Object.freeze({
    NOT_APPLICABLE: "na",
    PUBLISHED: "published",
    UNPUBLISHED: "unpublished",
});

/**
 * Normalizes an arbitrary value to a lowercase string for case-insensitive matching.
 * Does not trim, matching the Networks list search grammar.
 * @param {unknown} value
 * @returns {string}
 */
const toSearchableText = (value) =>
    value == null ? "" : String(value).toLowerCase();

/**
 * Filters network-config rows by a free-text search against client name and
 * pre-enrollment URL name. Matching is case-insensitive and substring-based.
 * An empty criteria string returns the original list unchanged.
 *
 * @param {Array<Object>} networkConfigs - In-memory network-config rows to filter.
 * @param {string} searchCriteria - Raw user-typed search text.
 * @returns {Array<Object>} The subset of `networkConfigs` matching the criteria.
 */
export const filterNetworkConfigsBySearchCriteria = (
    networkConfigs,
    searchCriteria,
) => {
    if (!Array.isArray(networkConfigs)) {
        return [];
    }

    const lowerSearchCriteria = toSearchableText(searchCriteria);
    if (lowerSearchCriteria === "") {
        return networkConfigs;
    }

    return networkConfigs.filter((client) => {
        const clientName = toSearchableText(client?.clientName);
        const oeUrlName = toSearchableText(client?.oeUrlName);

        return (
            clientName.includes(lowerSearchCriteria) ||
            oeUrlName.includes(lowerSearchCriteria)
        );
    });
};

/**
 * Compares two network-config rows on a named field using case-insensitive
 * string order. Missing fields sort as empty strings.
 *
 * @param {Object} leftRow
 * @param {Object} rightRow
 * @param {string} field - Row property to compare (e.g. `clientName`).
 * @returns {number} Negative when `leftRow` sorts first, positive when `rightRow` sorts first, otherwise 0.
 */
export const compareNetworkConfigRows = (leftRow, rightRow, field) => {
    const leftValue = toSearchableText(leftRow?.[field]);
    const rightValue = toSearchableText(rightRow?.[field]);

    return leftValue.localeCompare(rightValue);
};

/**
 * Returns a new array of network-config rows ordered by `sortedField`.
 * When `sortedField` is empty, the original list is returned unchanged.
 *
 * @param {Array<Object>} networkConfigs
 * @param {string | null} sortedField
 * @param {boolean} isSortDescending
 * @returns {Array<Object>}
 */
export const sortNetworkConfigs = (
    networkConfigs,
    sortedField,
    isSortDescending,
) => {
    if (!Array.isArray(networkConfigs)) {
        return [];
    }

    if (!sortedField) {
        return networkConfigs;
    }

    const orderedRows = [...networkConfigs].sort((leftRow, rightRow) =>
        compareNetworkConfigRows(leftRow, rightRow, sortedField),
    );

    if (isSortDescending) {
        orderedRows.reverse();
    }

    return orderedRows;
};

/**
 * Advances the Networks list three-state sort: unsorted → ascending → descending → unsorted.
 * Clicking a different field starts that field at ascending.
 *
 * @param {string | null} currentSortedField
 * @param {boolean} isSortDescending
 * @param {string} clickedField
 * @returns {{ sortedField: string | null, isSortDescending: boolean }}
 */
export const cycleNetworkListSort = (
    currentSortedField,
    isSortDescending,
    clickedField,
) => {
    if (currentSortedField === clickedField) {
        if (isSortDescending) {
            return { sortedField: null, isSortDescending: false };
        }

        return { sortedField: clickedField, isSortDescending: true };
    }

    return { sortedField: clickedField, isSortDescending: false };
};

/**
 * Formats a plain cell value, substituting an empty string for missing values.
 * @param {unknown} value
 * @returns {string}
 */
export const formatNetworkCellValue = (value) => value ?? "";

/**
 * Maps a publication status label to a visual tone token.
 * @param {unknown} status
 * @returns {string} A {@link NetworkStatusTone} value.
 */
export const getNetworkStatusTone = (status) => {
    switch (String(status)) {
        case NetworkPublicationStatus.PUBLISHED:
            return NetworkStatusTone.PUBLISHED;
        case NetworkPublicationStatus.UNPUBLISHED:
            return NetworkStatusTone.UNPUBLISHED;
        default:
            return NetworkStatusTone.NOT_APPLICABLE;
    }
};

/**
 * Builds the search-results count label shown only when search is non-empty.
 * @param {number} resultCount
 * @returns {string}
 */
export const formatNetworkResultsFoundLabel = (resultCount) => {
    const count = Number.isFinite(resultCount) ? resultCount : 0;
    const resultWord = count === 1 ? "result" : "results";

    return `${count} ${resultWord} found`;
};

/**
 * Maps one getClientConfigs response item to a Networks list grid row.
 * @param {object | null | undefined} clientConfig
 * @returns {object}
 */
export const mapClientConfigToNetworkRow = (clientConfig) => {
    const clientName = clientConfig?.name ?? "";
    const clientId = clientConfig?.clientId ?? "";
    const siteUrl = clientConfig?.preEnrollment?.siteURL;
    const oeUrlName = siteUrl ?? NetworkPublicationStatus.NOT_APPLICABLE;

    let status = NetworkPublicationStatus.NOT_APPLICABLE;
    if (siteUrl) {
        status = clientConfig?.preEnrollment?.publish
            ? NetworkPublicationStatus.PUBLISHED
            : NetworkPublicationStatus.UNPUBLISHED;
    }

    return {
        id: `${clientId}#${clientName}`,
        clientName,
        clientId,
        oeUrlName,
        status,
    };
};

/**
 * Maps a getClientConfigs response body into sorted Networks list rows.
 * @param {Array<object> | null | undefined} clientConfigs
 * @returns {Array<object>}
 */
export const mapClientConfigsToNetworkRows = (clientConfigs) => {
    if (!Array.isArray(clientConfigs)) {
        return [];
    }

    return clientConfigs
        .map(mapClientConfigToNetworkRow)
        .sort((leftRow, rightRow) =>
            leftRow.clientName.localeCompare(rightRow.clientName),
        );
};

// --- from networkConfigUtils.js ---

/**
 * Directory types a network row can declare.
 * @readonly
 * @enum {string}
 */
export const NetworkDirectoryType = Object.freeze({
    CENTIVO: "Centivo",
    EXTERNAL: "External/RBP",
    MIXED: "Centivo + External/RBP",
});

/**
 * Builds a fresh, empty network-config row.
 * The row id is injected so the function stays deterministic and testable.
 *
 * @param {string} rowId - Stable identity for React list keys.
 * @returns {Object} A new empty network configuration row.
 */
const createNetworkSeriesSlots = (seriesCount) =>
    Array.from({ length: Math.max(seriesCount, 0) }, () => "");

/**
 * Builds a fresh, empty network-config row with series slots sized to `seriesCount`.
 * @param {string} rowId
 * @param {number} [seriesCount=2]
 * @returns {Object}
 */
export const createEmptyNetworkConfig = (rowId, seriesCount = 2) => ({
    rowId,
    name: "",
    directoryType: "",
    includePreEnrollmentSite: null,
    networkSeries: createNetworkSeriesSlots(seriesCount),
    preferredNetworkCodes: [],
    rbpAdministrator: "",
    thirdPartyNetwork: "",
    requireVpcModule: null,
});

/**
 * Appends a new empty network row to the list without mutating the input.
 *
 * @param {Array<Object>} networkConfigs - Current rows.
 * @param {string} rowId - Stable identity for the appended row.
 * @returns {Array<Object>} A new array with the empty row appended.
 */
export const appendEmptyNetworkConfig = (
    networkConfigs,
    rowId,
    seriesCount = 2,
) => {
    const safeConfigs = Array.isArray(networkConfigs) ? networkConfigs : [];

    return [...safeConfigs, createEmptyNetworkConfig(rowId, seriesCount)];
};

/**
 * Removes the row at `index` without mutating the input. When the removal would
 * empty the list, a single fresh empty row is returned so the form always keeps
 * at least one network block.
 *
 * @param {Array<Object>} networkConfigs - Current rows.
 * @param {number} index - Row index to remove.
 * @param {string} fallbackRowId - Row id used when the list would become empty.
 * @returns {Array<Object>} A new array with the row removed.
 */
export const removeNetworkConfigAtIndex = (
    networkConfigs,
    index,
    fallbackRowId,
    seriesCount = 2,
) => {
    const safeConfigs = Array.isArray(networkConfigs) ? networkConfigs : [];
    const remaining = safeConfigs.filter((_, rowIndex) => rowIndex !== index);

    if (remaining.length === 0) {
        return [createEmptyNetworkConfig(fallbackRowId, seriesCount)];
    }

    return remaining;
};

/**
 * Resolves which network-series subforms a directory type reveals.
 *
 * @param {string} directoryType - One of {@link NetworkDirectoryType}.
 * @returns {{ showCentivo: boolean, showExternal: boolean }}
 */
export const resolveNetworkSeriesVisibility = (directoryType) => ({
    showCentivo:
        directoryType === NetworkDirectoryType.CENTIVO ||
        directoryType === NetworkDirectoryType.MIXED,
    showExternal:
        directoryType === NetworkDirectoryType.EXTERNAL ||
        directoryType === NetworkDirectoryType.MIXED,
});

/**
 * Whether an External/RBP row must ask the VPC-module question. Matches the
 * source rule: External-only directory with a third-party network other than
 * "None".
 *
 * @param {string} directoryType - One of {@link NetworkDirectoryType}.
 * @param {string} thirdPartyNetwork - Selected third-party network value.
 * @returns {boolean}
 */
export const resolveExternalNeedsVpc = (directoryType, thirdPartyNetwork) =>
    directoryType === NetworkDirectoryType.EXTERNAL &&
    Boolean(thirdPartyNetwork) &&
    thirdPartyNetwork !== "None";

/**
 * Tooltip message displayed on the Pre-enrollment tab when it is disabled.
 * @constant {string}
 */
export const PRE_ENROLLMENT_TAB_DISABLED_TOOLTIP =
    "This tab is disabled because no networks are configured to be included on the pre-enrollment site. Include at least one Centivo or Centivo + External network to enable.";

/**
 * Informative helper message shown when External/RBP directory type is selected.
 * @constant {string}
 */
export const EXTERNAL_DIRECTORY_PRE_ENROLLMENT_HELPER =
    "External/RBP directories cannot be included on pre-enrollment sites.";

/**
 * Whether the Pre-enrollment tab is enabled: at least one network is explicitly
 * included on the pre-enrollment site.
 *
 * @param {Array<Object>} networkConfigs - Current rows.
 * @returns {boolean}
 */
export const resolvePreEnrollmentTabEnabled = (networkConfigs) => {
    if (!Array.isArray(networkConfigs)) {
        return false;
    }

    return networkConfigs.some(
        (config) => config?.includePreEnrollmentSite === true,
    );
};

/**
 * Returns the explanatory tooltip when the Pre-enrollment tab is disabled,
 * or undefined when enabled.
 *
 * @param {Array<Object>} networkConfigs - Current rows.
 * @returns {string | undefined}
 */
export const resolvePreEnrollmentTabDisabledTooltip = (networkConfigs) => {
    return resolvePreEnrollmentTabEnabled(networkConfigs)
        ? undefined
        : PRE_ENROLLMENT_TAB_DISABLED_TOOLTIP;
};

/**
 * Extracts selectable network-name options from named rows, de-duplicated by
 * case-insensitive trimmed name and preserving first-seen order.
 *
 * @param {Array<Object>} networkConfigs - Current rows.
 * @returns {Array<{ title: string, value: string }>}
 */
export const parseNetworkNameOptions = (networkConfigs) => {
    if (!Array.isArray(networkConfigs)) {
        return [];
    }

    const seenNames = new Set();
    const options = [];

    networkConfigs.forEach((config) => {
        const trimmedName = (config?.name ?? "").trim();
        if (trimmedName === "") {
            return;
        }

        const dedupeKey = trimmedName.toLowerCase();
        if (seenNames.has(dedupeKey)) {
            return;
        }

        seenNames.add(dedupeKey);
        options.push({ title: trimmedName, value: config?.rowId ?? trimmedName });
    });

    return options;
};

/**
 * Tooltip message displayed on the Plan affiliation tab when it is disabled.
 * @constant {string}
 */
export const PLAN_AFFILIATION_TAB_DISABLED_TOOLTIP =
    "This tab is disabled because no networks have been configured. Enter a network name in the Networks tab to enable plan affiliation.";

/**
 * Whether the Plan-affiliation tab is enabled: at least one named network
 * exists to affiliate plans against.
 *
 * @param {Array<Object>} networkConfigs - Current rows.
 * @returns {boolean}
 */
export const resolvePlanAffiliationTabEnabled = (networkConfigs) =>
    parseNetworkNameOptions(networkConfigs).length > 0;

/**
 * Returns the explanatory tooltip when the Plan affiliation tab is disabled,
 * or undefined when enabled.
 *
 * @param {Array<Object>} networkConfigs - Current rows.
 * @returns {string | undefined}
 */
export const resolvePlanAffiliationTabDisabledTooltip = (networkConfigs) => {
    return resolvePlanAffiliationTabEnabled(networkConfigs)
        ? undefined
        : PLAN_AFFILIATION_TAB_DISABLED_TOOLTIP;
};

/**
 * Whether a single network row has the minimum fields a save requires:
 * a trimmed name, a directory type, and an explicit pre-enrollment choice.
 *
 * @param {Object} config - Network configuration row.
 * @returns {boolean}
 */
export const isNetworkConfigComplete = (config) => {
    const hasName = Boolean((config?.name ?? "").trim());
    const hasDirectoryType = Boolean(config?.directoryType);
    const hasPreEnrollmentChoice =
        config?.includePreEnrollmentSite === true ||
        config?.includePreEnrollmentSite === false;

    return hasName && hasDirectoryType && hasPreEnrollmentChoice;
};

/**
 * Whether the Save action is enabled: a client name is present and every
 * network row is complete.
 *
 * @param {Object} params
 * @param {string} params.clientName - Current client name.
 * @param {Array<Object>} params.networkConfigs - Current rows.
 * @returns {boolean}
 */
export const resolveClientConfigSaveEnabled = ({
    clientName,
    networkConfigs,
}) => {
    const hasClientName = Boolean((clientName ?? "").trim());
    const hasRows = Array.isArray(networkConfigs) && networkConfigs.length > 0;

    if (!hasClientName || !hasRows) {
        return false;
    }

    return networkConfigs.every(isNetworkConfigComplete);
};

/**
 * Whether saving should first warn that no network is included on the
 * pre-enrollment site: at least one network is configured and none include it.
 *
 * @param {Array<Object>} networkConfigs - Current rows.
 * @returns {boolean}
 */
export const resolveExcludesAllPreEnrollmentSites = (networkConfigs) => {
    if (!Array.isArray(networkConfigs) || networkConfigs.length === 0) {
        return false;
    }

    const hasConfiguredNetwork = networkConfigs.some((config) =>
        Boolean(config?.directoryType),
    );

    return hasConfiguredNetwork && !resolvePreEnrollmentTabEnabled(networkConfigs);
};

/**
 * Whether any affiliation plan still lacks a selected network.
 *
 * @param {Array<Object>} plans - Plan-affiliation rows.
 * @returns {boolean}
 */
export const hasUnaffiliatedPlans = (plans) => {
    if (!Array.isArray(plans) || plans.length === 0) {
        return false;
    }

    return plans.some((plan) => !plan?.networkAffiliation);
};

/**
 * Maps getNetworkSeriesOptions response into a keyed options map.
 * @param {Array<{ name?: string, values?: Array<string> }> | null | undefined} rawSeries
 * @returns {Record<string, Array<string>>}
 */
export const mapNetworkSeriesOptionsResponse = (rawSeries) => {
    if (!Array.isArray(rawSeries)) {
        return {};
    }

    return rawSeries.reduce((optionsByName, series) => {
        const name = series?.name;
        if (!name) {
            return optionsByName;
        }

        optionsByName[name] = Array.isArray(series.values) ? series.values : [];
        return optionsByName;
    }, {});
};

export const SINGLE_SELECT_SERIES_KEYS = Object.freeze([
    "Core network 100 series",
    "Wisconsin network 600 series",
    "400 series",
]);

/**
 * Checks whether a given series key allows only a single selected code.
 * @param {string} seriesKey
 * @returns {boolean}
 */
export const isSingleSelectSeriesKey = (seriesKey) =>
    SINGLE_SELECT_SERIES_KEYS.includes(seriesKey);

/**
 * Normalizes one API network-series slot based on its single vs multi-select series type.
 * @param {unknown} value
 * @param {boolean} [isSingleSelect=false]
 * @returns {string | Array<string>}
 */
export const normalizeApiNetworkSeriesSlot = (value, isSingleSelect = false) => {
    if (isSingleSelect) {
        if (Array.isArray(value)) {
            return value[0] != null ? String(value[0]) : "";
        }
        return value == null ? "" : String(value);
    }

    if (Array.isArray(value)) {
        return value.filter(Boolean).map(String);
    }

    if (value != null && value !== "") {
        return [String(value)];
    }

    return [];
};

/**
 * Pads or truncates API network-series values to the configured series key count.
 * @param {Array<unknown> | null | undefined} apiNetworkSeries
 * @param {Array<string>} seriesKeys
 * @returns {Array<string | Array<string>>}
 */
export const mapApiNetworkSeriesToFormSlots = (apiNetworkSeries, seriesKeys) => {
    const safeKeys = Array.isArray(seriesKeys) ? seriesKeys : [];

    return safeKeys.map((key, index) =>
        normalizeApiNetworkSeriesSlot(
            apiNetworkSeries?.[index],
            isSingleSelectSeriesKey(key),
        ),
    );
};

/**
 * Maps one getClientConfig networkConfigs item to a form row.
 * @param {object | null | undefined} apiRow
 * @param {Array<string>} seriesKeys
 * @param {string} [fallbackRowId]
 * @returns {object}
 */
export const mapApiNetworkConfigRowToFormRow = (
    apiRow,
    seriesKeys,
    fallbackRowId = "row-initial-1",
) => {
    const directoryType = apiRow?.directoryType ?? "";
    const thirdPartyNetwork = apiRow?.thirdPartyNetwork ?? "";
    let requireVpcModule = apiRow?.requireVpcModule ?? null;

    if (
        directoryType === NetworkDirectoryType.EXTERNAL &&
        thirdPartyNetwork !== "None" &&
        requireVpcModule == null
    ) {
        requireVpcModule = false;
    }

    return {
        rowId: apiRow?.uuid ?? fallbackRowId,
        name: apiRow?.name ?? "",
        directoryType,
        includePreEnrollmentSite: apiRow?.includePreEnrollmentSite ?? null,
        networkSeries: mapApiNetworkSeriesToFormSlots(
            apiRow?.networkSeries,
            seriesKeys,
        ),
        preferredNetworkCodes: Array.isArray(apiRow?.preferredNetworkCodes)
            ? apiRow.preferredNetworkCodes
            : [],
        rbpAdministrator: apiRow?.rbpAdministrator ?? "",
        thirdPartyNetwork,
        requireVpcModule,
    };
};

/**
 * Maps planAffiliations from getClientConfig into the CB form shape.
 * @param {object | null | undefined} planAffiliations
 * @param {Array<object>} networkConfigs
 * @returns {{ planType: string, selectedClientId: string, plans: Array<object> }}
 */
export const mapPlanAffiliationsToFormState = (
    planAffiliations,
    networkConfigs,
) => {
    if (!planAffiliations || planAffiliations.title === "NONE") {
        return {
            planType: "",
            selectedClientId: "",
            plans: [],
        };
    }

    const plans = Array.isArray(planAffiliations.plans)
        ? planAffiliations.plans.map((plan) => ({
              planId: plan?.planId ?? "",
              networkAffiliation:
                  plan?.networkAffiliation?.uuid ??
                  plan?.networkAffiliation?.value ??
                  "",
          }))
        : [];

    return {
        planType: planAffiliations.planType ?? "",
        selectedClientId:
            planAffiliations.title ?? planAffiliations.value ?? "",
        plans,
    };
};

/**
 * Maps pre-enrollment fields from getClientConfig into form state.
 * @param {object | null | undefined} preEnrollment
 * @returns {object}
 */
export const mapPreEnrollmentToFormState = (preEnrollment) => {
    const siteURL = preEnrollment?.siteURL ?? "";
    const customContent = Boolean(preEnrollment?.customContent);
    const publish = Boolean(preEnrollment?.publish);

    return {
        customContent,
        siteURL,
        publish,
        displayPcpActivationBadge:
            preEnrollment?.displayPcpActivationBadge !== false,
        displayReferralRequiredBadge:
            preEnrollment?.displayReferralRequiredBadge !== false,
        isOeUrlDisabled: Boolean(siteURL),
        isCustomContentDisabled: publish,
        isUrlValid: Boolean(siteURL),
        isUrlUnique: null,
        shouldShowUrlCheck: false,
    };
};

/**
 * Maps a getClientConfig response body into editable form state.
 * @param {object | null | undefined} clientConfig
 * @param {Array<string>} seriesKeys
 * @returns {{ clientName: string, networkConfigs: Array<object>, preEnrollment: object, planAffiliation: object }}
 */
export const mapClientConfigResponseToFormState = (clientConfig, seriesKeys) => {
    const safeSeriesKeys = Array.isArray(seriesKeys) ? seriesKeys : [];
    const apiNetworkConfigs = Array.isArray(clientConfig?.networkConfigs)
        ? clientConfig.networkConfigs
        : [];

    const networkConfigs = apiNetworkConfigs.length > 0
        ? apiNetworkConfigs.map((apiRow, index) =>
              mapApiNetworkConfigRowToFormRow(
                  apiRow,
                  safeSeriesKeys,
                  `row-loaded-${index + 1}`,
              ),
          )
        : [createEmptyNetworkConfig("row-initial-1", safeSeriesKeys.length)];

    const preEnrollment = mapPreEnrollmentToFormState(clientConfig?.preEnrollment);

    const planAffiliation = mapPlanAffiliationsToFormState(
        clientConfig?.planAffiliations,
        networkConfigs,
    );

    return {
        clientName: clientConfig?.name ?? "",
        networkConfigs,
        preEnrollment,
        planAffiliation,
    };
};

/**
 * Builds create-mode defaults once network-series keys are known.
 * @param {Array<string>} seriesKeys
 * @returns {{ clientName: string, networkConfigs: Array<object>, preEnrollment: object, planAffiliation: object }}
 */
export const createEmptyConfigFormState = (seriesKeys) => {
    const safeSeriesKeys = Array.isArray(seriesKeys) ? seriesKeys : [];

    return {
        clientName: "",
        networkConfigs: [
            createEmptyNetworkConfig("row-initial-1", safeSeriesKeys.length),
        ],
        preEnrollment: mapPreEnrollmentToFormState({}),
        planAffiliation: {
            planType: "",
            selectedClientId: "",
            plans: [],
        },
    };
};

const AFFILIATION_PLAN_TYPE_JAVELINA = "Javelina";
const AFFILIATION_PLAN_TYPE_HRP = "HRP";

/**
 * Whether a plan's network affiliation belongs to a client other than the current client.
 * @param {{ networkAffiliation?: { clientName?: string } | null }} plan
 * @param {string} currentClientName
 * @returns {boolean}
 */
export const validatePlanAffiliationBelongsToDifferentClient = (
    plan,
    currentClientName,
) =>
    Boolean(
        plan?.networkAffiliation?.clientName &&
            plan.networkAffiliation.clientName !== currentClientName,
    );

/**
 * Groups plans-grid rows into affiliation client dropdown options.
 * @param {{ jvPlans?: Array<object>, hrpPlans?: Array<object>, planType?: string, currentClientName?: string }} params
 * @returns {Array<{ id: string, label: string, planIds: Array<string>, disabled: boolean }>}
 */
export const buildPlanAffiliationClientOptions = ({
    jvPlans = [],
    hrpPlans = [],
    planType = "",
    currentClientName = "",
}) => {
    const plansForType =
        planType === AFFILIATION_PLAN_TYPE_HRP
            ? hrpPlans
            : planType === AFFILIATION_PLAN_TYPE_JAVELINA
              ? jvPlans
              : [];

    if (!Array.isArray(plansForType) || plansForType.length === 0) {
        return [];
    }

    const clientsById = new Map();

    plansForType.forEach((plan) => {
        const clientId = plan?.clientId ?? "";
        if (!clientId) {
            return;
        }

        const existingClient = clientsById.get(clientId) ?? {
            id: clientId,
            clientName: plan?.clientName ?? clientId,
            planIds: [],
            rawPlans: [],
        };

        const planId = plan?.planId ?? "";
        if (planId && !existingClient.planIds.includes(planId)) {
            existingClient.planIds.push(planId);
        }
        existingClient.rawPlans.push(plan);

        clientsById.set(clientId, existingClient);
    });

    return [...clientsById.values()]
        .map((client) => ({
            id: client.id,
            label: `${client.id} — ${client.clientName}`,
            planIds: client.planIds,
            disabled: client.rawPlans.some((plan) =>
                validatePlanAffiliationBelongsToDifferentClient(
                    plan,
                    currentClientName,
                ),
            ),
        }))
        .sort((leftClient, rightClient) =>
            leftClient.id.localeCompare(rightClient.id),
        );
};

/**
 * Whether URL validation should be skipped for a DOM event.
 * @param {{ eventType?: string, key?: string, isUrlUnique?: boolean | null }} params
 * @returns {boolean}
 */
export const validateSkipUrlCheckOnEvent = ({ eventType, key, isUrlUnique }) => {
    if (eventType === "blur" && isUrlUnique !== null) {
        return true;
    }

    if (eventType === "keydown" && key !== "Enter") {
        return true;
    }

    return false;
};

/**
 * Maps validateOeUrl response fields into pre-enrollment UI flags.
 * @param {{ unique?: boolean, valid?: boolean }} response
 * @returns {{ isUrlUnique: boolean, isUrlValid: boolean, shouldShowUrlCheck: boolean }}
 */
export const mapValidateOeUrlResponse = (response) => ({
    isUrlUnique: Boolean(response?.unique),
    isUrlValid: Boolean(response?.valid),
    shouldShowUrlCheck: true,
});

/**
 * Required key paths for an OE v2 create body (NONE-shell affiliations).
 * Network row keys are asserted per array element in tests.
 * @readonly
 */
export const OE_CREATE_REQUIRED_KEY_PATHS = Object.freeze([
    "name",
    "networkConfigs",
    "preEnrollment",
    "planAffiliations",
    "preEnrollment.siteURL",
    "preEnrollment.customContent",
    "preEnrollment.publish",
    "preEnrollment.hasCustomization",
    "preEnrollment.displayPcpActivationBadge",
    "preEnrollment.displayReferralRequiredBadge",
    "planAffiliations.title",
    "planAffiliations.value",
    "planAffiliations.plans",
    "planAffiliations.planType",
]);

/**
 * Required keys on each create wire networkConfigs row.
 * @readonly
 */
export const OE_CREATE_NETWORK_ROW_REQUIRED_KEYS = Object.freeze([
    "name",
    "directoryType",
    "networkSeries",
    "includePreEnrollmentSite",
]);

/**
 * Network-identity UUID paths forbidden on every OE v2 write body.
 * @readonly
 */
export const OE_V2_FORBIDDEN_KEY_PATHS = Object.freeze([
    "networkConfigs[].uuid",
    "planAffiliations.plans[].networkAffiliation.uuid",
    "planAffiliations.plans[].networkAffiliationOptions[].value",
]);

const PLAN_AFFILIATIONS_NONE_SHELL = Object.freeze({
    title: "NONE",
    value: "",
    plans: null,
    planType: "",
});

/**
 * Maps form network-series slots to the wire array (strings for single select, string arrays for multi select).
 * @param {Array<string | Array<string>> | null | undefined} networkSeries
 * @param {Array<string>} [seriesKeys]
 * @returns {Array<string | Array<string>>}
 */
export const mapFormNetworkSeriesToWire = (networkSeries, seriesKeys = []) => {
    if (!Array.isArray(networkSeries)) {
        return [];
    }

    return networkSeries.map((slot, index) => {
        const key = seriesKeys[index];
        const isSingle = key != null ? isSingleSelectSeriesKey(key) : !Array.isArray(slot);

        if (isSingle) {
            if (Array.isArray(slot)) {
                return slot[0] != null ? String(slot[0]) : "";
            }
            return slot == null ? "" : String(slot);
        }

        if (Array.isArray(slot)) {
            return slot.filter(Boolean).map(String);
        }

        if (slot != null && slot !== "") {
            return [String(slot)];
        }

        return [];
    });
};

/**
 * Maps one form network row to a v2 OE wire row (no uuid / rowId).
 * @param {object | null | undefined} row
 * @param {Array<string>} [seriesKeys]
 * @returns {object}
 */
export const mapFormNetworkConfigRowToWire = (row, seriesKeys = []) => {
    const directoryType = row?.directoryType ?? "";
    const visibility = resolveNetworkSeriesVisibility(directoryType);
    const wireRow = {
        name: row?.name ?? "",
        directoryType,
        networkSeries: mapFormNetworkSeriesToWire(row?.networkSeries, seriesKeys),
        includePreEnrollmentSite: Boolean(row?.includePreEnrollmentSite),
    };

    if (visibility.showCentivo && Array.isArray(row?.preferredNetworkCodes)) {
        wireRow.preferredNetworkCodes = [...row.preferredNetworkCodes];
    }

    if (visibility.showExternal) {
        wireRow.rbpAdministrator = row?.rbpAdministrator ?? "";
        wireRow.thirdPartyNetwork = row?.thirdPartyNetwork ?? "";
        wireRow.requireVpcModule = Boolean(row?.requireVpcModule);
    }

    return wireRow;
};

/**
 * Maps form pre-enrollment state to the OE wire shape (omits UI-only URL flags).
 * @param {object | null | undefined} preEnrollment
 * @returns {object}
 */
export const mapFormPreEnrollmentToWire = (preEnrollment) => ({
    siteURL: preEnrollment?.siteURL ?? "",
    customContent: Boolean(preEnrollment?.customContent),
    publish: Boolean(preEnrollment?.publish),
    hasCustomization:
        preEnrollment?.hasCustomization == null
            ? false
            : Boolean(preEnrollment.hasCustomization),
    displayPcpActivationBadge:
        preEnrollment?.displayPcpActivationBadge == null
            ? true
            : Boolean(preEnrollment.displayPcpActivationBadge),
    displayReferralRequiredBadge:
        preEnrollment?.displayReferralRequiredBadge == null
            ? true
            : Boolean(preEnrollment.displayReferralRequiredBadge),
});

/**
 * Resolves a form affiliation value (rowId or name) to a network display name.
 * @param {string} affiliationValue
 * @param {Array<object>} networkConfigs
 * @returns {string}
 */
const resolveNetworkNameFromAffiliationValue = (
    affiliationValue,
    networkConfigs,
) => {
    const safeConfigs = Array.isArray(networkConfigs) ? networkConfigs : [];
    const match = safeConfigs.find(
        (row) =>
            row?.rowId === affiliationValue || row?.name === affiliationValue,
    );

    return match?.name ?? "";
};

/**
 * Builds name-based planAffiliations for create when the form has plans.
 * Omits network-identity UUID fields.
 * @param {object} planAffiliation
 * @param {Array<object>} networkConfigs
 * @param {string} clientName
 * @returns {object}
 */
const mapFormPlanAffiliationToCreateWire = (
    planAffiliation,
    networkConfigs,
    clientName,
) => {
    const plans = Array.isArray(planAffiliation?.plans)
        ? planAffiliation.plans
        : [];
    const selectedClientId = planAffiliation?.selectedClientId ?? "";
    const safeConfigs = Array.isArray(networkConfigs) ? networkConfigs : [];

    const networkAffiliationOptions = safeConfigs
        .filter((row) => (row?.name ?? "").trim() !== "")
        .map((row) => ({
            title: row.name,
            isSelected: false,
        }));

    return {
        title: selectedClientId,
        value: selectedClientId,
        planType: planAffiliation?.planType ?? "",
        plans: plans.map((plan) => {
            const networkName = resolveNetworkNameFromAffiliationValue(
                plan?.networkAffiliation ?? "",
                safeConfigs,
            );
            const options = networkAffiliationOptions.map((option) => ({
                title: option.title,
                isSelected: option.title === networkName,
            }));

            return {
                planId: plan?.planId ?? "",
                clientId: selectedClientId,
                clientName,
                networkAffiliation: {
                    networkName,
                    clientName,
                },
                networkAffiliationOptions: options,
            };
        }),
    };
};

/**
 * Builds the OE v2 create request body from Networks Config form state.
 * Never copies rowId or network-identity UUID fields onto the wire.
 * @param {{
 *   clientName?: string,
 *   networkConfigs?: Array<object>,
 *   preEnrollment?: object,
 *   planAffiliation?: object,
 * }} formState
 * @returns {object}
 */
export const buildOeClientConfigCreateBody = (formState = {}) => {
    const clientName = formState.clientName ?? "";
    const networkConfigs = Array.isArray(formState.networkConfigs)
        ? formState.networkConfigs
        : [];
    const planAffiliation = formState.planAffiliation ?? {};
    const formPlans = Array.isArray(planAffiliation.plans)
        ? planAffiliation.plans
        : [];

    const planAffiliations =
        formPlans.length === 0
            ? { ...PLAN_AFFILIATIONS_NONE_SHELL }
            : mapFormPlanAffiliationToCreateWire(
                  planAffiliation,
                  networkConfigs,
                  clientName,
              );

    return {
        name: clientName,
        networkConfigs: networkConfigs.map((row) =>
            mapFormNetworkConfigRowToWire(row),
        ),
        preEnrollment: mapFormPreEnrollmentToWire(formState.preEnrollment),
        planAffiliations,
    };
};

/**
 * Required key paths for an OE v2 update body (top-level + preEnrollment + affiliation shell).
 * @readonly
 */
export const OE_UPDATE_REQUIRED_KEY_PATHS = Object.freeze([
    "name",
    "networkConfigs",
    "preEnrollment",
    "planAffiliations",
    "preEnrollment.siteURL",
    "preEnrollment.customContent",
    "preEnrollment.publish",
    "preEnrollment.hasCustomization",
    "preEnrollment.displayPcpActivationBadge",
    "preEnrollment.displayReferralRequiredBadge",
    "planAffiliations.title",
    "planAffiliations.value",
    "planAffiliations.plans",
    "planAffiliations.planType",
]);

/**
 * Required keys on each update wire planAffiliations.plans row (no forbidden UUID fields).
 * @readonly
 */
export const OE_UPDATE_PLAN_ROW_REQUIRED_KEYS = Object.freeze([
    "clientId",
    "clientName",
    "completed",
    "isFrom",
    "planId",
    "planName",
    "planStartDate",
    "planEndDate",
    "planUuid",
    "portalUrl",
    "productType#planId#configType",
    "started",
    "supportHours",
    "phoneNumberMemberServices",
    "networkAffiliation",
    "networkAffiliationOptions",
]);

const PRODUCT_TYPE_PLAN_ID_CONFIG_TYPE_KEY = "productType#planId#configType";

/**
 * Merges a plansAll grid row into a lookup entry (does not include network UUIDs).
 * @param {object} gridRow
 * @returns {object}
 */
const mapGridRowToLookupEntry = (gridRow) => {
    const clientId = gridRow?.clientId ?? "";
    const planId = gridRow?.planId ?? "";
    const planUuid =
        gridRow?.planUuid ??
        gridRow?.id ??
        (clientId && planId ? `${clientId}#${planId}` : "");

    return {
        clientId,
        clientName: gridRow?.clientName ?? "",
        planId,
        planName: gridRow?.planName ?? "",
        planStartDate: gridRow?.planStartDate ?? null,
        planEndDate: gridRow?.planEndDate ?? null,
        planUuid,
        portalUrl: gridRow?.portalUrl ?? "",
        supportHours: gridRow?.supportHours ?? "",
        phoneNumberMemberServices: gridRow?.phoneNumberMemberServices ?? "",
        completed: Boolean(gridRow?.completed),
        started: Boolean(gridRow?.started),
        isFrom: gridRow?.isFrom ?? "",
        [PRODUCT_TYPE_PLAN_ID_CONFIG_TYPE_KEY]:
            gridRow?.[PRODUCT_TYPE_PLAN_ID_CONFIG_TYPE_KEY] ?? "",
    };
};

/**
 * Merges a hydrated GET planAffiliations.plans row into a lookup entry.
 * Prefer hydrated values for round-trip fields; never keep network-identity UUIDs.
 * @param {object} existing
 * @param {object} hydratedPlan
 * @returns {object}
 */
const mergeHydratedPlanIntoLookupEntry = (existing, hydratedPlan) => {
    const clientId = hydratedPlan?.clientId ?? existing.clientId ?? "";
    const planId = hydratedPlan?.planId ?? existing.planId ?? "";
    const planUuid =
        hydratedPlan?.planUuid ??
        existing.planUuid ??
        (clientId && planId ? `${clientId}#${planId}` : "");

    return {
        ...existing,
        clientId,
        clientName: hydratedPlan?.clientName ?? existing.clientName ?? "",
        planId,
        planName: hydratedPlan?.planName ?? existing.planName ?? "",
        planStartDate:
            hydratedPlan?.planStartDate !== undefined
                ? hydratedPlan.planStartDate
                : (existing.planStartDate ?? null),
        planEndDate:
            hydratedPlan?.planEndDate !== undefined
                ? hydratedPlan.planEndDate
                : (existing.planEndDate ?? null),
        planUuid,
        portalUrl: hydratedPlan?.portalUrl ?? existing.portalUrl ?? "",
        supportHours: hydratedPlan?.supportHours ?? existing.supportHours ?? "",
        phoneNumberMemberServices:
            hydratedPlan?.phoneNumberMemberServices ??
            existing.phoneNumberMemberServices ??
            "",
        completed:
            hydratedPlan?.completed !== undefined
                ? Boolean(hydratedPlan.completed)
                : Boolean(existing.completed),
        started:
            hydratedPlan?.started !== undefined
                ? Boolean(hydratedPlan.started)
                : Boolean(existing.started),
        isFrom: hydratedPlan?.isFrom ?? existing.isFrom ?? "",
        [PRODUCT_TYPE_PLAN_ID_CONFIG_TYPE_KEY]:
            hydratedPlan?.[PRODUCT_TYPE_PLAN_ID_CONFIG_TYPE_KEY] ??
            existing[PRODUCT_TYPE_PLAN_ID_CONFIG_TYPE_KEY] ??
            "",
    };
};

/**
 * Builds a plans lookup map for OE v2 update affiliation metadata.
 * @param {{ jvPlans?: Array<object>, hrpPlans?: Array<object>, planType?: string }} grids
 * @param {Array<object>} [hydratedPlans]
 * @returns {Map<string, object>}
 */
export const buildOeUpdatePlansLookup = (grids = {}, hydratedPlans = []) => {
    const planType = grids.planType ?? "";
    const plansForType =
        planType === AFFILIATION_PLAN_TYPE_HRP
            ? grids.hrpPlans
            : planType === AFFILIATION_PLAN_TYPE_JAVELINA
              ? grids.jvPlans
              : [];

    const lookup = new Map();

    const putEntry = (entry) => {
        if (!entry?.planId) {
            return;
        }
        lookup.set(entry.planId, entry);
        if (entry.clientId) {
            lookup.set(`${entry.clientId}#${entry.planId}`, entry);
        }
        if (entry.planUuid) {
            lookup.set(entry.planUuid, entry);
        }
    };

    if (Array.isArray(plansForType)) {
        plansForType.forEach((gridRow) => {
            putEntry(mapGridRowToLookupEntry(gridRow));
        });
    }

    if (Array.isArray(hydratedPlans)) {
        hydratedPlans.forEach((hydratedPlan) => {
            const planId = hydratedPlan?.planId ?? "";
            const clientId = hydratedPlan?.clientId ?? "";
            const existing =
                lookup.get(
                    clientId && planId ? `${clientId}#${planId}` : planId,
                ) ??
                lookup.get(planId) ??
                {};
            putEntry(mergeHydratedPlanIntoLookupEntry(existing, hydratedPlan));
        });
    }

    return lookup;
};

/**
 * Builds name-based planAffiliations for update with full plan metadata.
 * @param {object} planAffiliation
 * @param {Array<object>} networkConfigs
 * @param {string} clientName
 * @param {Map<string, object>} plansLookup
 * @returns {object}
 */
const mapFormPlanAffiliationToUpdateWire = (
    planAffiliation,
    networkConfigs,
    clientName,
    plansLookup,
) => {
    const plans = Array.isArray(planAffiliation?.plans)
        ? planAffiliation.plans
        : [];
    const selectedClientId = planAffiliation?.selectedClientId ?? "";
    const safeConfigs = Array.isArray(networkConfigs) ? networkConfigs : [];
    const lookup = plansLookup instanceof Map ? plansLookup : new Map();

    const networkAffiliationOptions = safeConfigs
        .filter((row) => (row?.name ?? "").trim() !== "")
        .map((row) => ({
            title: row.name,
            isSelected: false,
        }));

    return {
        title: selectedClientId,
        value: selectedClientId,
        planType: planAffiliation?.planType ?? "",
        plans: plans.map((plan) => {
            const planId = plan?.planId ?? "";
            const meta =
                lookup.get(
                    selectedClientId && planId
                        ? `${selectedClientId}#${planId}`
                        : planId,
                ) ??
                lookup.get(planId) ??
                {};
            const networkName = resolveNetworkNameFromAffiliationValue(
                plan?.networkAffiliation ?? "",
                safeConfigs,
            );
            const options = networkAffiliationOptions.map((option) => ({
                title: option.title,
                isSelected: option.title === networkName,
            }));
            const clientId = meta.clientId || selectedClientId;
            const planUuid =
                meta.planUuid ||
                (clientId && planId ? `${clientId}#${planId}` : "");

            return {
                clientId,
                clientName: meta.clientName || clientName,
                completed: Boolean(meta.completed),
                isFrom: meta.isFrom ?? "",
                planId,
                planName: meta.planName ?? "",
                planStartDate: meta.planStartDate ?? null,
                planEndDate: meta.planEndDate ?? null,
                planUuid,
                portalUrl: meta.portalUrl ?? "",
                [PRODUCT_TYPE_PLAN_ID_CONFIG_TYPE_KEY]:
                    meta[PRODUCT_TYPE_PLAN_ID_CONFIG_TYPE_KEY] ?? "",
                started: Boolean(meta.started),
                supportHours: meta.supportHours ?? "",
                phoneNumberMemberServices:
                    meta.phoneNumberMemberServices ?? "",
                networkAffiliation: {
                    networkName,
                    clientName,
                },
                networkAffiliationOptions: options,
            };
        }),
    };
};

/**
 * Builds the OE v2 update request body from Networks Config form state.
 * Never copies rowId or network-identity UUID fields onto the wire.
 * @param {{
 *   clientName?: string,
 *   networkConfigs?: Array<object>,
 *   preEnrollment?: object,
 *   planAffiliation?: object,
 * }} formState
 * @param {{ plansLookup?: Map<string, object> }} [metadata]
 * @returns {object}
 */
export const buildOeClientConfigUpdateBody = (
    formState = {},
    metadata = {},
) => {
    const clientName = formState.clientName ?? "";
    const networkConfigs = Array.isArray(formState.networkConfigs)
        ? formState.networkConfigs
        : [];
    const planAffiliation = formState.planAffiliation ?? {};
    const formPlans = Array.isArray(planAffiliation.plans)
        ? planAffiliation.plans
        : [];
    const plansLookup =
        metadata.plansLookup instanceof Map
            ? metadata.plansLookup
            : new Map();

    const planAffiliations =
        formPlans.length === 0
            ? { ...PLAN_AFFILIATIONS_NONE_SHELL }
            : mapFormPlanAffiliationToUpdateWire(
                  planAffiliation,
                  networkConfigs,
                  clientName,
                  plansLookup,
              );

    return {
        name: clientName,
        networkConfigs: networkConfigs.map((row) =>
            mapFormNetworkConfigRowToWire(row),
        ),
        preEnrollment: mapFormPreEnrollmentToWire(formState.preEnrollment),
        planAffiliations,
    };
};

